import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useContext, useMemo, useSyncExternalStore } from 'react'
import { createSharePointStore, SharePointResolution, SharePointStore } from './sharePointStore'

// A store that holds nothing rather than undefined, so a component rendered outside the provider simply misses the
// cache instead of crashing the whole tree, the same trade OfficeEventHandlerContext makes (HAM-4310). It is
// deliberately stateless: a real store at module scope would be a session cache outliving the React tree.
const NO_STORE: SharePointStore = {
    getRevision: () => 0,
    get: () => undefined,
    resolve: (driveId, load) => (driveId ? load() : Promise.resolve(undefined)),
    invalidateFailed: () => undefined,
    subscribe: () => () => undefined,
}

const SharePointContext = createContext<SharePointStore>(NO_STORE)

/** Holds the resolved site and list per drive id for the session. */
export function SharePointProvider(props: PropsWithChildren): ReactElement {
    const store = useMemo(() => createSharePointStore(), [])

    return <SharePointContext.Provider value={store}>{props.children}</SharePointContext.Provider>
}

/**
 * Subscribes to the store, so every caller re-renders once a drive id resolves. The snapshot is the revision
 * counter rather than the values themselves: those live in a Map that is mutated in place, which
 * useSyncExternalStore would either miss or loop on.
 */
export function useSharePointCache(): {
    get: (driveId: string | undefined) => SharePointResolution | undefined
    resolve: SharePointStore['resolve']
    invalidateFailed: () => void
} {
    const store = useContext(SharePointContext)
    const revision = useSyncExternalStore(store.subscribe, store.getRevision)

    return useMemo(
        () => ({
            get: (driveId: string | undefined) => store.get(driveId),
            resolve: store.resolve,
            invalidateFailed: store.invalidateFailed,
        }),
        [store, revision],
    )
}

export default SharePointContext
